import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { addToast } from "@/components/base/toast";
import { useTRPC } from "@/integrations/trpc/react";
import { copyAsync } from "@/lib/clipboard";
import { invalidateAllTournamentQueries } from "@/lib/tournament-cache";

export function useCompletePoolsMutation() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation({
    ...trpc.tournaments.completePools.mutationOptions(),
    onSuccess: (data) => {
      invalidateAllTournamentQueries(queryClient, trpc);
      if (data.syncFailed) {
        addToast({
          variant: "error",
          title: "Bracket sync failed",
          description:
            "Pool standings were saved, but the playoff bracket could not be updated. Try re-completing the pool(s).",
        });
      } else if (data.wildcardAssignFailed) {
        addToast({
          variant: "error",
          title: "Wildcard auto-assign failed",
          description:
            "Pool standings were saved, but wildcards could not be auto-assigned. Try assigning them manually.",
        });
      } else {
        addToast({ variant: "success", title: "Pools completed" });
      }
    },
    onError: (error) => {
      addToast({
        variant: "error",
        title: "Unable to complete pools",
        description: error.message,
      });
    },
  });
}

export function useSetDivisionStatusMutation() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation({
    ...trpc.tournaments.setDivisionStatus.mutationOptions(),
    onSuccess: (_data, variables) => {
      invalidateAllTournamentQueries(queryClient, trpc);
      addToast({
        variant: "success",
        title:
          variables.status === "running"
            ? "Division started"
            : "Division paused",
      });
    },
  });
}

export function useCopyPlayerEmails() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useCallback(
    (tournamentId: number, tournamentDivisionIds?: number[]) => {
      const emailsPromise = queryClient
        .fetchQuery(
          trpc.tournaments.getPlayerEmails.queryOptions({
            tournamentId,
            tournamentDivisionIds,
          }),
        )
        .then((result) => result.emails);

      copyAsync(emailsPromise.then((emails) => emails.join(", ")))
        .then(() =>
          emailsPromise.then((emails) => {
            if (emails.length === 0) {
              addToast({ variant: "info", title: "No player emails found" });
            } else {
              addToast({
                variant: "success",
                title: "Copied!",
                description: tournamentDivisionIds
                  ? `${emails.length} player email${emails.length !== 1 ? "s" : ""}`
                  : `${emails.length} player emails from all divisions`,
              });
            }
          }),
        )
        .catch(() =>
          addToast({ variant: "error", title: "Failed to copy emails" }),
        );
    },
    [queryClient, trpc],
  );
}
